import * as pc from "playcanvas";
import type { GameClient } from "../net/GameClient";
import type { FirstPersonController } from "../player/FirstPersonController";
import { resumeAudioContext, unlockAudioOnGesture } from "./audioContext";

const WALK_SOUND_URL = "/audio/fps-walk.wav";
const LAND_SOUND_URL = "/audio/player-land.mp3";

/** Landing thud volume — louder than the walk loop, quieter than a death grunt. */
const LAND_VOLUME = 0.5;
/** Landing thud volume while sneaking (shift-walk / crouch / scoped) — a soft
 *  tap, not a slam, so dropping off a ledge quietly doesn't broadcast a thud. */
const LAND_VOLUME_QUIET = 0.12;

/** Position delta (m) between snapshots that counts as "moving". */
const MOVE_EPS = 0.015;
/** How long a single movement reading keeps the peer "moving" (debounce
 *  the 32 Hz snapshots into a continuous signal for the faster frame loop). */
const MOVE_GRACE = 0.25;
/** How long the silent-movement flags keep suppressing footsteps after they
 *  last read true. The server clears `walking` whenever no direction key is
 *  held, so a mid-sneak direction change (release one key, press the next)
 *  flickers the flag off for a few ticks — this hold bridges that gap. */
const SILENT_HOLD = 0.3;

interface RemoteStep {
  entity: pc.Entity;
  lastX: number;
  lastZ: number;
  movingGrace: number;
  silentHold: number;
  playing: boolean;
}

/**
 * Footsteps — first-pass movement sound (test phase).
 *
 * Loops a walk sound while a player runs. Silent when shift-walking or
 * crouching (CS:GO convention). The local player hears their own steps
 * at a fixed volume; each opponent's steps are 3D positional via a
 * PlayCanvas AudioListener on the camera + a sound emitter that follows
 * the peer — so footsteps swell as they approach and fade with distance.
 *
 * Also plays a one-shot landing thud (2D, local only) the frame the local
 * player touches down from a jump or fall — driven by the controller's
 * `consumeJustLanded()` edge.
 */
export class Footsteps {
  private readonly app: pc.AppBase;
  private readonly client: GameClient;
  private readonly fpc: FirstPersonController;
  private asset: pc.Asset | null = null;
  private landAsset: pc.Asset | null = null;
  private readonly localEmitter: pc.Entity;
  private localPlaying = false;
  private loggedFirstStep = false;
  private readonly remote = new Map<string, RemoteStep>();
  private readonly tickFn: (dt: number) => void;

  constructor(app: pc.AppBase, client: GameClient, fpc: FirstPersonController) {
    this.app = app;
    this.client = client;
    this.fpc = fpc;

    // 3D audio needs a listener; the camera is the player's ears.
    const camera = fpc.getCameraEntity();
    if (!camera.audiolistener) camera.addComponent("audiolistener");

    // Local steps + landing are 2D (fixed volume) — parented to the camera.
    // The sound component is created up front so each clip can register its
    // slot as it loads, independent of which asset resolves first.
    this.localEmitter = new pc.Entity("footsteps-local");
    camera.addChild(this.localEmitter);
    this.localEmitter.addComponent("sound");
    if (this.localEmitter.sound) this.localEmitter.sound.positional = false;

    this.app.assets.loadFromUrl(WALK_SOUND_URL, "audio", (err, asset) => {
      if (err || !asset) {
        console.warn("[Footsteps] failed to load walk sound:", WALK_SOUND_URL, err);
        return;
      }
      this.asset = asset;
      this.localEmitter.sound?.addSlot("walk", {
        asset: asset.id,
        volume: 0.32,
        loop: true,
        autoPlay: false,
        overlap: false,
      });
      console.log("[Footsteps] walk sound ready:", WALK_SOUND_URL, "id=", asset.id);
    });

    this.app.assets.loadFromUrl(LAND_SOUND_URL, "audio", (err, asset) => {
      if (err || !asset) {
        console.warn("[Footsteps] failed to load land sound:", LAND_SOUND_URL, err);
        return;
      }
      this.landAsset = asset;
      this.localEmitter.sound?.addSlot("land", {
        asset: asset.id,
        volume: LAND_VOLUME,
        loop: false,
        autoPlay: false,
        overlap: true, // a fresh thud each touchdown, even if one's still ringing
      });
      console.log("[Footsteps] land sound ready:", LAND_SOUND_URL, "id=", asset.id);
    });

    // Force the audio context awake on the first user gesture (browser
    // autoplay policy). Shared + idempotent across all audio managers.
    unlockAudioOnGesture(this.app);

    this.tickFn = (dt: number) => this.update(dt);
    this.app.on("update", this.tickFn);
  }

  destroy(): void {
    this.app.off("update", this.tickFn);
    for (const r of this.remote.values()) r.entity.destroy();
    this.remote.clear();
    this.localEmitter.destroy();
  }

  private update(dt: number): void {
    // ── Local landing — one-shot thud on the airborne → grounded edge ──
    if (this.fpc.consumeJustLanded() && this.landAsset) {
      resumeAudioContext(this.app);
      const inst = this.localEmitter.sound?.play("land");
      if (inst) inst.volume = this.fpc.isQuietMovement() ? LAND_VOLUME_QUIET : LAND_VOLUME;
    }

    if (!this.asset) return;

    // ── Local player — own footsteps, fixed volume ──
    const localRun = this.fpc.isFootstepActive();
    const localSound = this.localEmitter.sound;
    if (localSound) {
      if (localRun && !this.localPlaying) {
        resumeAudioContext(this.app); // belt-and-suspenders before first play
        localSound.play("walk");
        this.localPlaying = true;
        if (!this.loggedFirstStep) {
          this.loggedFirstStep = true;
          console.log("[Footsteps] local step play started");
        }
      } else if (!localRun && this.localPlaying) {
        localSound.stop();
        this.localPlaying = false;
      }
    }

    // ── Remote players — positional, distance-attenuated ──
    const selfId = this.client.sessionId;
    const seen = new Set<string>();
    this.client.forEachKnownPlayer((p) => {
      if (p.sessionId === selfId) return;
      seen.add(p.sessionId);

      let r = this.remote.get(p.sessionId);
      if (!r) {
        r = this.createRemote(p.sessionId);
        this.remote.set(p.sessionId, r);
      }

      // Emit from roughly the peer's torso so panning reads naturally.
      r.entity.setPosition(p.x, p.y + 0.6, p.z);

      const moved = Math.hypot(p.x - r.lastX, p.z - r.lastZ) > MOVE_EPS;
      r.lastX = p.x;
      r.lastZ = p.z;

      // Shift-walk / crouch = silent (CS:GO). The `walking` flag drops to
      // false for a few ticks when direction keys gap during a sneak turn,
      // while leftover movingGrace from the sneak still reads "moving" —
      // which leaked a footstep burst only the opponent could hear. Hold
      // the silence across the flicker and never let silent movement arm
      // the grace window.
      const silent = !!p.walking || !!p.crouching; // shift / crouch / scoped
      r.silentHold = silent ? SILENT_HOLD : Math.max(0, r.silentHold - dt);
      const quiet = silent || r.silentHold > 0;
      r.movingGrace = quiet
        ? 0
        : moved
          ? MOVE_GRACE
          : Math.max(0, r.movingGrace - dt);

      const alive = (p.health ?? 0) > 0;
      const run = r.movingGrace > 0 && alive;

      const snd = r.entity.sound;
      if (snd) {
        if (run && !r.playing) {
          snd.play("walk");
          r.playing = true;
        } else if (!run && r.playing) {
          snd.stop();
          r.playing = false;
        }
      }
    });

    for (const [sid, r] of this.remote) {
      if (!seen.has(sid)) {
        r.entity.destroy();
        this.remote.delete(sid);
      }
    }
  }

  private createRemote(sid: string): RemoteStep {
    const e = new pc.Entity(`footsteps-${sid}`);
    e.addComponent("sound");
    const s = e.sound;
    if (s) {
      // 3D positional — swells closer, fades with distance.
      s.positional = true;
      s.distanceModel = pc.DISTANCE_LINEAR;
      s.refDistance = 2.5; // full volume within 2.5 m
      s.maxDistance = 32;  // inaudible past 32 m
      s.rollOffFactor = 1.0;
      s.addSlot("walk", {
        asset: this.asset!.id,
        volume: 1.0,
        loop: true,
        autoPlay: false,
        overlap: false,
      });
    }
    this.app.root.addChild(e);
    return { entity: e, lastX: 0, lastZ: 0, movingGrace: 0, silentHold: 0, playing: false };
  }
}
